import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { auth } from '../config/firebase'; 
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { usuarioService } from '../services/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // LOGIN TRADICIONAL
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        alert("Por favor, verifique seu e-mail antes de acessar.");
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (error) {
      alert("Falha ao entrar. Verifique se o e-mail e a senha estão corretos.");
    } finally {
      setLoading(false);
    }
  };

  // LOGIN COM GOOGLE
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await usuarioService.sincronizarGoogle({
        uid: user.uid,
        nome: user.displayName,
        email: user.email,
        telefone: user.phoneNumber || ''
      });

      navigate('/dashboard');
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        console.error("Erro Google Sync:", error);
        alert("Erro ao tentar entrar com o Google.");
      }
    } finally {
      setLoading(false);
    }
  };

  // RECUPERAÇÃO DE SENHA
  const handleRecuperarSenha = async () => {
    if (!email) {
      alert("Por favor, digite seu e-mail no campo acima para redefinir a senha.");
      return;
    }
    
    try {
      setLoading(true);
      await usuarioService.recuperarSenha(email);
      alert("E-mail de recuperação enviado! Verifique sua caixa de entrada.");
    } catch (error) {
      alert(error.response?.data?.erro || "Erro ao tentar recuperar a senha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#F8F7FF] font-sans p-6 overflow-hidden">
      
      {/* Efeitos de fundo harmonizados com o roxo */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(#6D28D9_1px,transparent_1px)] bg-[size:40px_40px]"></div>
      <div className="absolute w-[400px] h-[400px] bg-gradient-to-r from-blue-600/10 to-purple-600/10 blur-[100px] rounded-full pointer-events-none -top-[10%] -right-[5%]"></div>

      {/* Botão Voltar */}
      <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 no-underline text-[#94a3b8] text-[10px] font-black uppercase tracking-[0.2em] transition-all hover:text-[#6D28D9]">
        <ArrowLeft size={14} /> Voltar ao Início
      </Link>

      <div className="relative z-10 w-full max-w-[440px] bg-white rounded-[40px] p-8 sm:p-12 border border-purple-50 shadow-[0_20px_50px_rgba(109,40,217,0.05)] animate-fade-in-up">
        
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-[#F5F3FF] px-3 py-1 rounded-full text-[#6D28D9] text-[9px] font-black uppercase tracking-[0.1em] mb-6">
            <span className="w-1.5 h-1.5 bg-[#6D28D9] rounded-full"></span> Acesso Restrito
          </div>
          <img src="/logo-sincro2.png" alt="Agenda Sincro" className="h-[70px] mx-auto mb-8" />
        </div>

        <form onSubmit={handleLogin}>
          {/* Email */}
          <div className="mb-6 text-left">
            <div className="text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-2 ml-1">E-mail Institucional</div>
            <div className="relative flex items-center group">
              <Mail size={18} className="absolute left-4 text-[#94a3b8] group-focus-within:text-[#6D28D9]" />
              <input 
                type="email" 
                className="w-full bg-[#F8F7FF] border border-slate-100 py-4 pl-12 pr-4 rounded-[18px] text-sm font-medium text-[#1e293b] outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-600/5"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Senha */}
          <div className="mb-6 text-left">
            <div className="flex justify-between text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-2 ml-1">
              <span>Senha</span>
              <span className="text-[#6D28D9] cursor-pointer hover:underline" onClick={handleRecuperarSenha}>Esqueci a Senha</span>
            </div>
            <div className="relative flex items-center group">
              <Lock size={18} className="absolute left-4 text-[#94a3b8] group-focus-within:text-[#6D28D9]" />
              <input 
                type={showPassword ? "text" : "password"} 
                className="w-full bg-[#F8F7FF] border border-slate-100 py-4 pl-12 pr-12 rounded-[18px] text-sm font-medium text-[#1e293b] outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-600/5"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-[#94a3b8] hover:text-[#6D28D9]">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="w-full py-4 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white rounded-[18px] text-[11px] font-black uppercase tracking-[0.2em] shadow-lg hover:opacity-90 transition-all disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Processando..." : "Entrar"}
          </button>
        </form>

        <div className="flex items-center my-6 text-[#94a3b8] text-[10px] font-bold uppercase tracking-[0.1em] before:flex-1 before:border-b before:border-slate-100 before:mr-3 after:flex-1 after:border-b after:border-slate-100 after:ml-3">
          ou continuar com
        </div>

        <button 
          type="button" 
          onClick={handleGoogleLogin} 
          className="w-full py-4 bg-white text-[#1e293b] border border-slate-100 rounded-[18px] text-sm font-bold flex items-center justify-center gap-3 hover:border-[#6D28D9] transition-all"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Acessar com o Google
        </button>

        <div className="mt-8 pt-6 border-t border-slate-50 text-xs font-bold text-[#94a3b8] uppercase tracking-[0.05em]">
          Não possui acesso? <Link to="/register" className="text-[#6D28D9] hover:underline">Solicitar Cadastro</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;